import React from 'react';
import PropTypes from 'prop-types';

import {
  getCampaignPromoUnitPrice,
  getCheckoutSubscriptionChargeCopy,
} from '../../../constants/elevatedCampaignPromoUi';
import { SUBSCRIPTION_TYPES } from '../../../constants/subscriptions';

const OrderSummary = ({
  selectedSubscription,
  providers = [],
  billingQuantity,
  showTitle,
  isMobile,
  showPaymentForm,
  showCampaignPromo = false,
  discount = null,
}) => {
  const providerCount =
    billingQuantity ?? (providers.length > 0 ? providers.length : 0);
  const listUnitPrice = selectedSubscription?.price || 0;
  const listTotal = listUnitPrice * providerCount;
  const listTotalCents = Math.round(listUnitPrice * 100) * providerCount;

  const hasDiscount = Boolean(discount);
  const discountCents = hasDiscount
    ? Math.round((listTotalCents * discount.percentOff) / 100)
    : 0;
  const totalDue = hasDiscount
    ? (listTotalCents - discountCents) / 100
    : listTotal;

  const promoUnitPrice =
    showCampaignPromo && selectedSubscription
      ? getCampaignPromoUnitPrice(selectedSubscription.id)
      : null;

  /** Legacy / no discount: single subtotal equals total due. Campaign + discount: list → discount → due. */
  const summaryPrimaryLabel = hasDiscount ? 'Total price' : 'Subtotal';
  const summaryPrimaryAmount = hasDiscount
    ? listTotal
    : showCampaignPromo && promoUnitPrice != null
      ? promoUnitPrice * providerCount
      : listTotal;
  const productLineAmount = summaryPrimaryAmount;

  const headerChargeCopy = getCheckoutSubscriptionChargeCopy(showCampaignPromo);

  return (
    <div className="flex w-full min-w-0 grow shrink-0 flex-col items-start gap-5">
      {/* Header Text */}
      {showTitle && (
        <p className="self-stretch break-words font-extralight text-sm leading-5 text-gray-90 lg:text-white">
          {headerChargeCopy}
        </p>
      )}

      {/* Subscription Info */}
      {!isMobile ? (
        <div className="flex items-center gap-3 shrink-0 self-stretch">
          <div className="w-full flex flex-col justify-center items-start grow shrink-0">
            <div className="flex min-w-0 items-start justify-between gap-3 self-stretch">
              <span className="min-w-0 flex-1 text-base font-semibold leading-6 text-gray-90 lg:text-white">
                Elevated Profile -{' '}
                {selectedSubscription
                  ? selectedSubscription.id === SUBSCRIPTION_TYPES.ANNUAL.id
                    ? 'Annual'
                    : 'Monthly'
                  : ''}
              </span>
              <span className="shrink-0 whitespace-nowrap text-base font-semibold leading-6 text-gray-90 lg:text-white">
                ${productLineAmount.toFixed(2)}
              </span>
            </div>
            <span className="self-stretch text-sm font-extralight leading-5 text-gray-90 lg:text-white">
              Qty: {providerCount || 0}
            </span>
          </div>
        </div>
      ) : (
        showPaymentForm && (
          <div className="flex items-center gap-3 shrink-0 self-stretch">
            <div className="w-full flex flex-col justify-center items-start grow shrink-0">
              <div className="flex shrink-0 items-start justify-between gap-2.5 self-stretch">
                <span className="text-base font-semibold leading-6 text-gray-90 lg:text-white">
                  {selectedSubscription?.label || ''} Details
                </span>
                <span className="text-base font-semibold leading-6 text-gray-90 lg:text-white">
                  ${productLineAmount.toFixed(2)}
                </span>
              </div>
              <span className="self-stretch text-sm font-extralight leading-5 text-gray-90 lg:text-white">
                Healthgrades Elevated Profile
              </span>
            </div>
          </div>
        )
      )}

      {/* Separator */}
      {!isMobile && (
        <div className="flex flex-col items-start gap-2.5 py-0.5 self-stretch">
          <div className="w-full h-px bg-gray-60 lg:bg-white self-stretch" />
        </div>
      )}
      {/* Subtotal / total price (list) */}
      {!(showPaymentForm && isMobile) && (
        <div className="flex min-w-0 items-start justify-between gap-3 self-stretch">
          <span className="min-w-0 flex-1 break-words text-sm font-extralight leading-5 text-gray-90 lg:text-white">
            {summaryPrimaryLabel}
          </span>
          <span className="shrink-0 whitespace-nowrap text-sm font-bold leading-5 text-gray-90 lg:text-white">
            ${summaryPrimaryAmount.toFixed(2)}
          </span>
        </div>
      )}

      {/* Campaign discount */}
      {hasDiscount && !(showPaymentForm && isMobile) && (
        <div className="flex min-w-0 items-start justify-between gap-3 self-stretch">
          <span className="min-w-0 flex-1 break-words text-sm font-extralight leading-5 text-gray-90 lg:text-white">
            Discount ({discount.promotionCode})
          </span>
          <span className="shrink-0 whitespace-nowrap text-sm font-bold leading-5 text-gray-90 lg:text-white">
            -${(discountCents / 100).toFixed(2)}
          </span>
        </div>
      )}

      {/* Separator */}
      <div className="flex flex-col items-start gap-2.5 py-0.5 self-stretch">
        <div className="w-full h-px bg-gray-60 lg:bg-white self-stretch" />
      </div>

      {/* Total */}
      <div className="flex min-w-0 items-start justify-between gap-3 self-stretch">
        <span className="min-w-0 flex-1 text-sm font-extralight leading-5 text-gray-90 lg:text-white">
          Total due today
        </span>
        <span className="shrink-0 whitespace-nowrap text-sm font-bold leading-5 text-gray-90 lg:text-white">
          ${totalDue.toFixed(2)}
        </span>
      </div>
    </div>
  );
};

OrderSummary.propTypes = {
  selectedSubscription: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    price: PropTypes.number,
  }),
  providers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  billingQuantity: PropTypes.number,
  showTitle: PropTypes.bool,
  isMobile: PropTypes.bool,
  showPaymentForm: PropTypes.bool,
  showCampaignPromo: PropTypes.bool,
  discount: PropTypes.shape({
    promotionCode: PropTypes.string.isRequired,
    percentOff: PropTypes.number.isRequired,
  }),
};

export default OrderSummary;
